import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { formatCurrency } from '../utils/formatCurrency';
import { Plus, Copy, AlertTriangle } from 'lucide-react';
import { format, startOfMonth } from 'date-fns';
import { pt } from 'date-fns/locale';
import { useTheme } from '../context/ThemeContext';

export default function Budgets() {
  const { cardShape } = useTheme();
  const [budgets, setBudgets] = useState([]);
  const [unbudgeted, setUnbudgeted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [showForm, setShowForm] = useState(false);
  const [editBudget, setEditBudget] = useState(null);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ category_id: '', amount: '', month: '', rollover_enabled: false, alert_at: 80 });

  const fetchBudgets = async () => {
    setLoading(true);
    const res = await api.get('/budgets', { params: { month } });
    setBudgets(res.data.budgets || res.data);
    setUnbudgeted(res.data.unbudgeted || res.data.unbudgeted_categories || []);
    setLoading(false);
  };

  useEffect(() => { fetchBudgets(); }, [month]);
  useEffect(() => { api.get('/categories', { params: { type: 'expense' } }).then(r => setCategories(r.data)); }, []);

  const totalBudgeted = budgets.reduce((s, b) => s + parseFloat(b.amount), 0);
  const totalSpent = budgets.reduce((s, b) => s + parseFloat(b.spent || 0), 0);
  const totalPct = totalBudgeted > 0 ? ((totalSpent / totalBudgeted) * 100).toFixed(0) : 0;
  const categoryOptions = editBudget ? categories : unbudgeted;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, month, amount: parseFloat(form.amount) };
    if (editBudget) {
      await api.put(`/budgets/${editBudget.id}`, payload);
    } else {
      await api.post('/budgets', payload);
    }
    setShowForm(false); setEditBudget(null);
    fetchBudgets();
  };

  const copyPrevious = async () => {
    await api.post('/budgets/copy-previous', { month });
    fetchBudgets();
  };

  const progressColor = (pct) => pct >= 100 ? 'danger' : pct >= 80 ? 'warning' : '';

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Orçamentos</h1>
          <p className="page-subtitle">{format(new Date(month + '-01'), 'MMMM yyyy', { locale: pt })}</p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button className="btn btn-secondary" onClick={copyPrevious}>
            <Copy size={16} /> Copiar mês anterior
          </button>
          <button className="btn btn-primary" onClick={() => { setEditBudget(null); setForm({ category_id: '', amount: '', month, rollover_enabled: false, alert_at: 80 }); setShowForm(true); }}>
            <Plus size={18} /> Novo Orçamento
          </button>
        </div>
      </div>

      {/* Seletor de mês */}
      <div className="filter-bar">
        <input type="month" className="form-input" style={{ width: 180 }} value={month} onChange={e => setMonth(e.target.value)} />
      </div>

      {/* Resumo */}
      <div className={`card mb-md variant-${cardShape}`}>
        <div className="flex-between mb-sm">
          <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>Total Neste Mês</span>
          <span className="text-mono" style={{ fontSize: 14, fontWeight: 600 }}>{totalPct}%</span>
        </div>
        <div className="progress-bar" style={{ height: 12 }}>
          <div className={`progress-fill ${progressColor(totalPct)}`} style={{ width: `${Math.min(totalPct, 100)}%` }} />
        </div>
        <div className="flex-between mt-sm">
          <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>Gasto: {formatCurrency(totalSpent)}</span>
          <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>Limite: {formatCurrency(totalBudgeted)}</span>
        </div>
      </div>

      {/* Budgets List */}
      {loading ? (
        [1, 2, 3].map(i => <div key={i} className="skeleton mb-sm" style={{ height: 100, borderRadius: 16 }} />)
      ) : budgets.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <p className="empty-state-title">Sem orçamentos</p>
            <p className="empty-state-text">Crie orçamentos para controlar os seus gastos.</p>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {budgets.map(b => {
            const pct = parseFloat(b.percentage || 0);
            const remaining = parseFloat(b.amount) - parseFloat(b.spent || 0);
            return (
              <div key={b.id} className={`card variant-${cardShape}`} style={{ cursor: 'pointer' }}
                onClick={() => { setEditBudget(b); setForm({ ...b }); setShowForm(true); }}>
                <div className="flex-between mb-sm">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{b.category?.name || 'Categoria'}</span>
                    {pct >= 80 && <AlertTriangle size={14} color="var(--accent-amber)" />}
                  </div>
                  <span className="text-mono" style={{ fontSize: 14, fontWeight: 600, color: pct >= 100 ? 'var(--accent-red)' : 'var(--text-primary)' }}>
                    {pct.toFixed(0)}%
                  </span>
                </div>
                <div className="progress-bar">
                  <div className={`progress-fill ${progressColor(pct)}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                </div>
                <div className="flex-between mt-sm">
                  <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{formatCurrency(b.spent || 0)} gasto</span>
                  <span style={{ fontSize: 12, color: remaining >= 0 ? 'var(--accent-green)' : 'var(--accent-red)' }}>
                    {remaining >= 0 ? `${formatCurrency(remaining)} restante` : `${formatCurrency(Math.abs(remaining))} acima`}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editBudget ? 'Editar Orçamento' : 'Novo Orçamento'}</h2>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowForm(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Categoria</label>
                  <select className="form-input form-select" value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })} required>
                    <option value="">Selecionar...</option>
                    {categoryOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Limite Mensal</label>
                  <input className="form-input form-input-money" type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Alerta em (%)</label>
                    <input className="form-input" type="number" min="0" max="100" value={form.alert_at} onChange={e => setForm({ ...form, alert_at: parseInt(e.target.value) })} />
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--text-secondary)', cursor: 'pointer', paddingTop: 24 }}>
                    <input type="checkbox" checked={form.rollover_enabled} onChange={e => setForm({ ...form, rollover_enabled: e.target.checked })} />
                    Rollover
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{editBudget ? 'Guardar' : 'Criar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
